/**
 * Single source of truth for JOBUWIN legal documents on the frontend.
 *
 * Each document version is an immutable markdown file in this folder. Never edit a published
 * `vX.Y.md` — add a new file and a new entry, then bump `currentVersion`. `contentHash` is the
 * SHA-256 of the markdown file (see `scripts/hashLegalDocs.js`) and must match the API registry.
 */
import termsV1_0 from "./terms/v1.0.md";
import termsV1_1 from "./terms/v1.1.md";
import privacyV1_0 from "./privacy/v1.0.md";
import privacyV1_1 from "./privacy/v1.1.md";
import cookiesV1_0 from "./cookies/v1.0.md";

export const LEGAL_DOCUMENTS = {
  terms: {
    type: "terms",
    label: "Terms of Use",
    path: "/terms",
    currentVersion: "1.1",
    effectiveDate: "1 June 2026",
    requiresReacceptance: true,
    versions: {
      "1.0": {
        content: termsV1_0,
        contentHash: "865a2fcb141edf73680bfa9819afa6addd033e8f84a7f43602dd12e67eb0d516",
        effectiveDate: "31 May 2026",
        effectiveDateIso: "2026-05-31",
      },
      "1.1": {
        content: termsV1_1,
        contentHash: "7dbae53ed508dd696a02606c8e0018cde3b7e3fbb6300620f8b9dbee010f5996",
        effectiveDate: "1 June 2026",
        effectiveDateIso: "2026-06-01",
      },
    },
  },
  privacy: {
    type: "privacy",
    label: "Privacy Policy",
    path: "/privacy",
    currentVersion: "1.1",
    effectiveDate: "1 June 2026",
    requiresReacceptance: true,
    versions: {
      "1.0": {
        content: privacyV1_0,
        contentHash: "65381d36baaa0803a415b48d4c7afa108d3f48b14a8bb67a030e24921058f5b6",
        effectiveDate: "31 May 2026",
        effectiveDateIso: "2026-05-31",
      },
      "1.1": {
        content: privacyV1_1,
        contentHash: "f6932750a7fc7a47374cdba56a85c75035cfa193545d6b8760e82222a71f924a",
        effectiveDate: "1 June 2026",
        effectiveDateIso: "2026-06-01",
      },
    },
  },
  cookies: {
    type: "cookies",
    label: "Cookie Policy",
    path: "/cookies",
    currentVersion: "1.0",
    effectiveDate: "1 June 2026",
    requiresReacceptance: false,
    versions: {
      "1.0": {
        content: cookiesV1_0,
        contentHash: "e6e02586381662c8d665ff012a3fc4f6bf1c4db5e1b26bbdc8280af2142cc64b",
        effectiveDate: "1 June 2026",
        effectiveDateIso: "2026-06-01",
      },
    },
  },
};

/** Documents users must accept at signup or when re-prompted (excludes informational policies). */
export const LEGAL_DOCUMENT_TYPES = ["terms", "privacy"];

export function getLegalDoc(type) {
  return LEGAL_DOCUMENTS[type] || null;
}

/** True when the route is a legal document page (current or versioned). */
export function isLegalDocumentPath(pathname = "") {
  const path = String(pathname || "");
  return Object.values(LEGAL_DOCUMENTS).some(
    (doc) => path === doc.path || path.startsWith(`${doc.path}/`)
  );
}

/** Resolve a specific version (or the current one) for a document type. */
export function getLegalVersion(type, version) {
  const doc = getLegalDoc(type);
  if (!doc) return null;
  const resolvedVersion = version && doc.versions[version] ? version : doc.currentVersion;
  const entry = doc.versions[resolvedVersion];
  if (!entry) return null;
  return {
    type: doc.type,
    label: doc.label,
    path: doc.path,
    version: resolvedVersion,
    isCurrent: resolvedVersion === doc.currentVersion,
    ...entry,
  };
}

/** Versions the FE is currently displaying, used as the signup acceptance payload. */
export function currentLegalVersions() {
  return LEGAL_DOCUMENT_TYPES.reduce((acc, type) => {
    acc[type] = LEGAL_DOCUMENTS[type].currentVersion;
    return acc;
  }, {});
}

/**
 * Given the versions a user has accepted (e.g. from `/auth/me`), decide whether they must
 * re-accept. A missing acceptance always requires consent; an older version only re-prompts
 * when that document is flagged `requiresReacceptance`.
 */
export function legalDocsNeedingAcceptance(acceptedVersions) {
  const accepted = acceptedVersions || {};
  return LEGAL_DOCUMENT_TYPES.filter((type) => {
    const doc = LEGAL_DOCUMENTS[type];
    const acceptedVersion = accepted[type] || null;
    if (acceptedVersion === doc.currentVersion) return false;
    if (!acceptedVersion) return true;
    return Boolean(doc.requiresReacceptance);
  });
}
